import { ARENA, ENEMY_TYPES, TUNING, UNIT_TYPES, WAVE_DEFINITIONS } from './definitions.js'

const distance = (_a, _b) => Math.hypot(_a.x - _b.x, _a.y - _b.y)

const moveToward = (_entity, _target, _speed, _delta) =>
{
    const offsetX = _target.x - _entity.x
    const offsetY = _target.y - _entity.y
    const remaining = Math.hypot(offsetX, offsetY)
    if(remaining < 0.001)
        return 0
    const step = Math.min(remaining, _speed * _delta)
    _entity.x += offsetX / remaining * step
    _entity.y += offsetY / remaining * step
    _entity.rotation = Math.atan2(- offsetX, offsetY)
    return remaining
}

export default class CombatSystem
{
    constructor(_state)
    {
        this.state = _state
        this.enemySequence = 0
    }

    start()
    {
        if(!this.state.canStartWave())
            return false
        const number = this.state.completedWaves + 1
        this.state.wave = {
            active: true,
            status: 'running',
            number,
            enemies: this.createEnemies(number),
            obeliskHealth: TUNING.obeliskHealth,
            obeliskMaxHealth: TUNING.obeliskHealth,
            elapsed: 0,
            result: null
        }
        this.state.trigger('waveStarted', [{ number, enemyCount: this.state.wave.enemies.length }])
        return true
    }

    createEnemies(_number)
    {
        const composition = WAVE_DEFINITIONS[_number] ? { ...WAVE_DEFINITIONS[_number] } : this.generateComposition(_number)
        const endlessLevel = Math.max(0, _number - 9)
        const healthScale = 1.08 ** endlessLevel
        const damageScale = 1.05 ** endlessLevel
        const enemies = []
        let spawnIndex = 0
        for(const [type, count] of Object.entries(composition))
        {
            const definition = ENEMY_TYPES[type]
            for(let index = 0; index < count && enemies.length < 40; index++)
            {
                const angle = spawnIndex * 2.399963
                const radius = 0.5 + (spawnIndex % 5) * 0.42
                const maximumHealth = definition.health * healthScale
                enemies.push({
                    id: `enemy${++this.enemySequence}`,
                    type,
                    x: ARENA.portal.x + Math.cos(angle) * radius,
                    y: ARENA.portal.y + Math.sin(angle) * radius,
                    rotation: 0,
                    health: maximumHealth,
                    maxHealth: maximumHealth,
                    damage: definition.damage * damageScale,
                    cooldown: (spawnIndex % 5) * 0.12,
                    alive: true
                })
                spawnIndex++
            }
        }
        return enemies
    }

    generateComposition(_number)
    {
        const level = _number - 9
        let budget = Math.round(22 * 1.05 ** level)
        const composition = { hollow: 0, wraith: 0, inquisitor: 0 }
        if(_number % 5 === 0)
        {
            composition.inquisitor = 1
            budget -= ENEMY_TYPES.inquisitor.cost
        }
        let index = 0
        while(budget > 0 && composition.hollow + composition.wraith < 39)
        {
            const type = index % 3 === 2 && budget >= 2 ? 'wraith' : 'hollow'
            composition[type]++
            budget -= ENEMY_TYPES[type].cost
            index++
        }
        return composition
    }

    rallyPosition(_unit)
    {
        const sameRole = this.state.units.filter((_item) => _item.type === _unit.type && _item.role === 'combat')
        const index = Math.max(0, sameRole.findIndex((_item) => _item.id === _unit.id))
        const rowWidth = _unit.type === 'brute' ? 4 : 6
        const row = Math.floor(index / rowWidth)
        const column = index % rowWidth
        const x = (column - (Math.min(rowWidth, sameRole.length) - 1) * 0.5) * 1.15
        const y = _unit.type === 'brute' ? -20.5 : _unit.type === 'guard' ? -19.2 : _unit.type === 'archer' ? -16.9 : -15.9
        return { x, y: y - row * 1.05 }
    }

    update(_delta)
    {
        const defenders = this.state.units.filter((_unit) => _unit.role === 'combat' && _unit.alive)
        if(!this.state.wave.active)
        {
            for(const defender of defenders)
                moveToward(defender, this.rallyPosition(defender), UNIT_TYPES[defender.type].speed, _delta)
            return
        }

        this.state.wave.elapsed += _delta
        const enemies = this.state.wave.enemies.filter((_enemy) => _enemy.alive)
        for(const defender of defenders)
            this.updateDefender(defender, enemies, _delta)
        for(const enemy of enemies)
        {
            if(enemy.alive)
                this.updateEnemy(enemy, _delta)
        }
        this.separate([...defenders, ...enemies])

        this.state.units = this.state.units.filter((_unit) => _unit.alive)
        this.state.wave.enemies = this.state.wave.enemies.filter((_enemy) => _enemy.alive)
        if(this.state.wave.obeliskHealth <= 0)
            this.resolve(false)
        else if(this.state.wave.enemies.length === 0)
            this.resolve(true)
    }

    separate(_entities)
    {
        const living = _entities.filter((_entity) => _entity.alive)
        for(let firstIndex = 0; firstIndex < living.length; firstIndex++)
        {
            for(let secondIndex = firstIndex + 1; secondIndex < living.length; secondIndex++)
            {
                const first = living[firstIndex]
                const second = living[secondIndex]
                let offsetX = second.x - first.x
                let offsetY = second.y - first.y
                let currentDistance = Math.hypot(offsetX, offsetY)
                if(currentDistance >= 0.58)
                    continue
                if(currentDistance < 0.001)
                {
                    offsetX = (firstIndex % 2 ? 1 : -1) * 0.01
                    offsetY = 0.01
                    currentDistance = Math.hypot(offsetX, offsetY)
                }
                const push = (0.58 - currentDistance) * 0.5
                const normalX = offsetX / currentDistance
                const normalY = offsetY / currentDistance
                first.x -= normalX * push
                first.y -= normalY * push
                second.x += normalX * push
                second.y += normalY * push
            }
        }
    }

    updateDefender(_unit, _enemies, _delta)
    {
        const definition = UNIT_TYPES[_unit.type]
        _unit.cooldown = Math.max(0, _unit.cooldown - _delta)
        let target = null
        let targetDistance = Infinity
        for(const enemy of _enemies)
        {
            const currentDistance = distance(_unit, enemy)
            if(enemy.alive && currentDistance < targetDistance)
            {
                target = enemy
                targetDistance = currentDistance
            }
        }
        if(!target)
            return
        if(targetDistance > definition.range)
        {
            moveToward(_unit, target, definition.speed, _delta)
            return
        }
        if(_unit.cooldown > 0)
            return

        _unit.cooldown = definition.cooldown
        const damage = definition.damage * this.state.combatMultiplier
        const victims = definition.area ? _enemies.filter((_enemy) => _enemy.alive && distance(_unit, _enemy) <= definition.area) : [target]
        for(const victim of victims)
            this.damageEnemy(victim, damage)
        if(definition.healing)
        {
            const injured = this.state.units
                .filter((_ally) => _ally.role === 'combat' && _ally.alive && _ally.health < _ally.maxHealth && distance(_unit, _ally) <= 6)
                .sort((_a, _b) => _a.health / _a.maxHealth - _b.health / _b.maxHealth)[0]
            if(injured)
                injured.health = Math.min(injured.maxHealth, injured.health + definition.healing * this.state.combatMultiplier)
        }
        this.state.trigger('unitAttacked', [{ unit: _unit, target, damage, ranged: definition.range > 2, area: definition.area ?? 0 }])
    }

    updateEnemy(_enemy, _delta)
    {
        const definition = ENEMY_TYPES[_enemy.type]
        _enemy.cooldown = Math.max(0, _enemy.cooldown - _delta)
        const defenders = this.state.units.filter((_unit) => _unit.role === 'combat' && _unit.alive)
        const brutes = defenders.filter((_unit) => _unit.type === 'brute' && distance(_enemy, _unit) <= UNIT_TYPES.brute.taunt)
        const candidates = brutes.length ? brutes : defenders
        let target = null
        let targetDistance = Infinity
        for(const defender of candidates)
        {
            const currentDistance = distance(_enemy, defender)
            if(currentDistance < targetDistance)
            {
                target = defender
                targetDistance = currentDistance
            }
        }
        if(!target)
        {
            target = ARENA.obelisk
            targetDistance = distance(_enemy, target)
        }
        if(targetDistance > definition.range)
        {
            moveToward(_enemy, target, definition.speed, _delta)
            return
        }
        if(_enemy.cooldown > 0)
            return
        _enemy.cooldown = definition.cooldown
        if(target === ARENA.obelisk)
        {
            this.state.wave.obeliskHealth = Math.max(0, this.state.wave.obeliskHealth - _enemy.damage)
            this.state.trigger('obeliskDamaged', [{ health: this.state.wave.obeliskHealth, maximum: this.state.wave.obeliskMaxHealth }])
        }
        else
            this.damageUnit(target, _enemy.damage)
        this.state.trigger('enemyAttacked', [{ enemy: _enemy, target, ranged: definition.range > 2 }])
    }

    damageEnemy(_enemy, _damage)
    {
        _enemy.health = Math.max(0, _enemy.health - _damage)
        if(_enemy.health > 0 || !_enemy.alive)
            return
        _enemy.alive = false
        this.state.trigger('enemyDied', [{ enemy: _enemy }])
    }

    damageUnit(_unit, _damage)
    {
        _unit.health = Math.max(0, _unit.health - _damage)
        if(_unit.health > 0 || !_unit.alive)
            return
        _unit.alive = false
        this.state.trigger('unitDied', [{ unit: _unit }])
    }

    resolve(_victory)
    {
        const number = this.state.wave.number
        this.state.wave.active = false
        this.state.wave.status = _victory ? 'victory' : 'defeat'
        if(_victory)
        {
            this.state.completedWaves++
            this.state.mastery = Math.max(0, this.state.completedWaves - 9)
            this.state.unlockProgress()
        }
        for(const unit of this.state.units)
        {
            if(unit.role !== 'combat' || !unit.alive)
                continue
            unit.maxHealth = UNIT_TYPES[unit.type].health * this.state.combatMultiplier
            unit.health = unit.maxHealth
        }
        this.state.wave.enemies = []
        this.state.wave.obeliskHealth = TUNING.obeliskHealth
        const result = { victory: _victory, number, mastery: this.state.mastery }
        this.state.wave.result = result
        this.state.trigger('waveResolved', [result])
    }
}
